import { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CheckCircle, XCircle, DownloadSimple, ShieldCheck, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Ledger() {
  const [entries, setEntries] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [integrity, setIntegrity] = useState(null);

  const load = useCallback(async () => {
    const [led, ver] = await Promise.all([api.get("/ledger"), api.get("/ledger/verify")]);
    setEntries(led.data.entries);
    setIsAdmin(led.data.is_admin);
    setIntegrity(ver.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    const { data } = await api.get("/ledger/verify");
    setIntegrity(data);
    toast[data.intact ? "success" : "error"](data.intact ? "Catena verificata: integra" : "Catena compromessa!");
  };

  const exportLedger = async () => {
    const { data } = await api.get("/ledger/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `certytrace-audit-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export generato per audit DPA");
  };

  const short = (h) => (h && h.length > 16 ? `${h.slice(0, 8)}…${h.slice(-6)}` : h);

  return (
    <AppShell breadcrumb="Registro di Audit">
      <div className="max-w-6xl">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-950">Registro di Audit</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Ledger immutabile con hash-chain SHA-256. {isAdmin ? "Vista operatore: tutte le attività." : "Vista delle tue attività."}
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="ledger-verify-btn" onClick={verify} variant="outline" className="rounded-sm border-zinc-300 gap-2">
              <ShieldCheck size={16} weight="duotone" /> Verifica catena
            </Button>
            <Button data-testid="ledger-export-btn" onClick={exportLedger} className="bg-zinc-950 text-white hover:bg-zinc-800 rounded-sm gap-2">
              <DownloadSimple size={16} weight="bold" /> Esporta (DPA)
            </Button>
          </div>
        </div>

        {integrity && (
          <div data-testid="ledger-integrity"
            className={`flex items-center gap-3 border rounded-sm px-4 py-3 mb-4 ${integrity.intact ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
            {integrity.intact ? <CheckCircle size={20} weight="fill" className="text-green-600" /> : <XCircle size={20} weight="fill" className="text-red-600" />}
            <div className="text-sm">
              <span className="font-medium text-zinc-900">{integrity.intact ? "Catena integra — nessuna manomissione" : `Catena rotta alla sequenza #${integrity.broken_at_sequence}`}</span>
              <span className="text-zinc-500 ml-2 font-mono text-xs">{integrity.total_entries} voci · verificato {new Date(integrity.verified_at).toLocaleTimeString("it-IT")}</span>
            </div>
          </div>
        )}

        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-200">
                <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Seq</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Timestamp</TableHead>
                {isAdmin && <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Utente</TableHead>}
                <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Soggetto</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Finalità</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Prev · Hash</TableHead>
                <TableHead className="font-mono text-[10px] uppercase tracking-wide text-zinc-500">Chain</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="ledger-table-body">
              {entries.length === 0 ? (
                <TableRow><TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-sm text-zinc-400 py-10">Nessuna voce nel registro.</TableCell></TableRow>
              ) : (
                entries.map((e) => (
                  <TableRow key={e.id} data-testid={`ledger-row-${e.sequence}`} className="border-zinc-100">
                    <TableCell className="font-mono text-xs text-zinc-500">#{e.sequence}</TableCell>
                    <TableCell className="text-xs text-zinc-600 whitespace-nowrap">{new Date(e.timestamp).toLocaleString("it-IT")}</TableCell>
                    {isAdmin && <TableCell className="text-xs text-zinc-600">{e.user_email}</TableCell>}
                    <TableCell className="text-sm text-zinc-900">
                      {e.target_name}
                      <div className="text-[10px] font-mono text-zinc-400">{e.target_id}</div>
                    </TableCell>
                    <TableCell className="text-xs text-zinc-600 max-w-[180px]">{e.business_purpose_label}</TableCell>
                    <TableCell className="font-mono text-[10px] text-zinc-500">
                      <div className="flex items-center gap-1 text-zinc-400"><LinkSimple size={11} /> {short(e.previous_hash)}</div>
                      <div className="text-zinc-800">{short(e.entry_hash)}</div>
                    </TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-600" /> OK
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppShell>
  );
}
